// Quick Create Party Modal
// Minimal party creation form for inline use from other forms
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useRef } from 'react';
import Modal, {
  ModalFormField,
  ModalInput,
  ModalSelect,
  ModalPrimaryButton,
  ModalSecondaryButton
} from '../shared/Modal';
import { apiPost } from '../../utils/api';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';

const PARTY_TYPES = [
  { value: 'licensee', label: 'Licensee' },
  { value: 'rights_holder', label: 'Rights Holder' },
  { value: 'author', label: 'Author' },
  { value: 'publisher', label: 'Publisher' },
  { value: 'agent', label: 'Agent' },
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'designer', label: 'Designer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'other', label: 'Other' },
];

const QuickCreatePartyModal = ({ isOpen, onClose, onCreated }) => {
  const [formData, setFormData] = useState({
    name: '',
    party_type: '',
    contact_email: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  // Keyboard shortcuts for quick submit
  useKeyboardShortcuts({
    'ctrl+Enter': () => {
      if (formRef.current && formData.name && formData.party_type && !loading) {
        formRef.current.requestSubmit();
      }
    },
    'cmd+Enter': () => {
      if (formRef.current && formData.name && formData.party_type && !loading) {
        formRef.current.requestSubmit();
      }
    },
  }, isOpen);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await apiPost('/api/v1/parties', {
        party: {
          name: formData.name,
          party_type: formData.party_type,
          contact_email: formData.contact_email || null,
          status: 'active',
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.errors?.join(', ') || 'Failed to create party');
      }

      const newParty = await response.json();

      // Reset form
      setFormData({ name: '', party_type: '', contact_email: '' });

      // Notify parent
      onCreated(newParty);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: '', party_type: '', contact_email: '' });
    setError(null);
    onClose();
  };

  const icon = (
    <svg className="w-4 h-4 text-[#a1c6a2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  );

  const footer = (
    <>
      <ModalPrimaryButton
        type="submit"
        form="quick-create-party-form"
        disabled={loading || !formData.name || !formData.party_type}
        loading={loading}
      >
        Create Party
      </ModalPrimaryButton>
      <ModalSecondaryButton type="button" onClick={handleClose}>
        Cancel
      </ModalSecondaryButton>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Quick Add Party"
      subtitle="New Record"
      icon={icon}
      footer={footer}
      maxWidth="sm:max-w-md"
    >
      <form id="quick-create-party-form" ref={formRef} onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 border border-[#c47474] bg-[rgba(196,116,116,0.1)] text-[#c47474] text-sm">
            {error}
          </div>
        )}

        <ModalFormField label="Party Name" required>
          <ModalInput
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g., Acme Licensing Corp"
            required
            autoFocus
          />
        </ModalFormField>

        <ModalFormField label="Party Type" required>
          <ModalSelect
            name="party_type"
            value={formData.party_type}
            onChange={handleChange}
            required
          >
            <option value="">-- Select Type --</option>
            {PARTY_TYPES.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </ModalSelect>
        </ModalFormField>

        <ModalFormField label="Contact Email">
          <ModalInput
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            placeholder="email@example.com (optional)"
          />
        </ModalFormField>

        <p
          className="text-[10px] text-[rgba(255,251,247,0.3)] mt-4"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          Press Cmd+Enter to submit
        </p>
      </form>
    </Modal>
  );
};

export default QuickCreatePartyModal;
